import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';

// ---------------------------------------------------------------------------
// Types (mirror backend/src/modules/dashboard/dashboard.service.ts DTOs)
// ---------------------------------------------------------------------------

export interface DashboardResponse {
  month: string;
  currentMonth: string;
  currency: string;
  cash: {
    balance: number | null;
    asOf: string | null;
    liabilities: number | null;
    trend: Array<{ month: string; balance: number | null }>;
  };
  ar: {
    outstanding: number;
    overdue: number;
    invoiceCount: number;
    overdueCount: number;
    aging: {
      current: number;
      days1_30: number;
      days31_60: number;
      days61_90: number;
      days90plus: number;
    };
  };
  /**
   * ⚠ **CASH.** Money that arrived, recognized on the payment date — the app's
   * one definition of revenue (backend `pnl/revenue.ts`). Invoiced and unpaid
   * is `ar` below: *billed, not yet revenue*. Any screen printing one of these
   * says which it is, in prose, once.
   */
  revenue: {
    /** Always `'cash'` — read it rather than hardcoding the word. */
    basis: 'cash';
    /** The API's own one-sentence statement of the basis. */
    basisNote: string;
    mtd: number;
    ytd: number;
    /**
     * The annual revenue GOAL from the cash plan, pro-rated: `goalMtd` is
     * goal ÷ 12, `goalYtd` is goal × elapsed months ÷ 12. It replaced an
     * income BUDGET summed from the deleted `BudgetLine` grid — this is
     * Jesse's own number and the same row /budget-forecast prints, so the two
     * screens cannot quote different targets.
     */
    goalMtd: number;
    goalYtd: number;
    /** A cash plan exists for the year — distinct from a goal of $0. */
    hasGoal: boolean;
    goalYear: number;
    forecastMtd: number;
    forecastYtd: number;
  };
  expenses: { mtd: number; ytd: number };
  /** Cash revenue − expenses. Equals the P&L's Net Profit on its cash default. */
  profit: { mtd: number; ytd: number };
  /** `revenue` is cash collected in the month. */
  series: Array<{ month: string; revenue: number; expenses: number }>;
  forecastVsActual: Array<{ month: string; actual: number | null; forecast: number | null }>;
  expenseBreakdown: Array<{ key: string; label: string; amount: number; share: number }>;
  /**
   * **STAFF UTILIZATION — billable ÷ CAPACITY**, the same number
   * `/api/team/utilization` returns for STAFF (`current.utilization`) from the
   * same shared computation.
   *
   * ⚠ It used to be billable ÷ TRACKED under the same field name, which put
   * 63% here and 49% on `/team` for the same 8 staff in the same month, under
   * identical titles and identical "target 75%" captions. `billableShare` is
   * that other ratio, now named: a diagnostic with **no target**.
   */
  utilization: {
    month: string;
    /** billable ÷ capacity — the ratio `target` judges. */
    utilization: number | null;
    capacityHours: number;
    /** billable ÷ tracked — never print `target` beside it. */
    billableShare: number | null;
    billableHours: number;
    totalHours: number;
    peopleTracking: number;
    avgHoursPerPersonPerWeek: number | null;
    target: number;
    previous: number | null;
    previousBillableShare: number | null;
  };
  /**
   * **Effective hourly rate** — `invoiced revenue ÷ ALL tracked hours`, over a
   * named year-to-date window.
   *
   * ⚠ **It does NOT share a population with `utilization` above.** Utilization
   * is staff only (the capacity question); this counts **everyone who tracked
   * time**, staff and contractors, because contractor hours produce revenue
   * (the output question). The two cards sit side by side on the dashboard, so
   * each names its population in words — never remove those labels.
   *
   * `revenue` is the same figure as `revenue.ytd` — the server hands both off
   * one computation, which is the P&L's (cash) income. ⚠ The numerator is CASH
   * COLLECTED since 2026-08-13; it used to be invoiced revenue, which made this
   * card quote a different revenue from the tile above it.
   */
  effectiveRate: {
    year: number;
    /** e.g. `Jan–Aug 2026` — the window, printed on the card. */
    windowLabel: string;
    revenue: number;
    totalHours: number;
    billableHours: number;
    staffHours: number;
    contractorHours: number;
    peopleTracking: number;
    /** revenue ÷ totalHours; null when nobody tracked time. */
    rate: number | null;
    /** revenue ÷ billableHours — the average bill rate achieved. The gap
     *  between this and `rate` IS the non-billable time. */
    billableRate: number | null;
    billableShare: number | null;
    /** The same months a year earlier, for the trend. */
    previous: {
      year: number;
      windowLabel: string;
      revenue: number;
      totalHours: number;
      rate: number | null;
    } | null;
  };
  /**
   * Support hours at risk, read off the BANK (the canonical support model), so
   * this card and `/support` can never disagree. `reason: 'allowance'` is the
   * one exception: a client with a monthly entitlement and no bank.
   */
  supportAtRisk: Array<{
    clientId: string;
    clientName: string;
    reason: 'overage' | 'exhausted' | 'low' | 'allowance';
    balanceHours: number | null;
    overageHours: number;
    pctRemaining: number | null;
    usedHours: number | null;
    allowanceHours: number | null;
  }>;
  lateInvoices: Array<{
    id: string;
    number: string;
    clientId: string;
    clientName: string;
    dueAmount: number;
    dueDate: string;
    daysOverdue: number;
  }>;
}

export const dashboardKeys = {
  all: ['dashboard'] as const,
  overview: (month: string) => [...dashboardKeys.all, 'overview', month] as const,
};

export function useDashboard(month: string) {
  return useQuery({
    queryKey: dashboardKeys.overview(month),
    queryFn: () => api.get<DashboardResponse>('/dashboard', { params: { month } }),
    placeholderData: (previous) => previous,
  });
}

/** Current month as YYYY-MM (local time — matches the backend default). */
export function currentMonthKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function addMonthsKey(key: string, delta: number): string {
  const [y, m] = key.split('-').map(Number);
  const total = y * 12 + (m - 1) + delta;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}
